<template>
  <div
    id="savingsWidget"
    ref="savingsWidgetRef"
    class="savingsWidget"
    :app-language="locale"
    :app-name="appName$"
    :app-version="version$"
  >
    <div class="savingsWidget__header">
      <div
        class="h2"
        v-text="$t('savingsWidget.upload.title')"
      />

      <div
        class="lead-2"
        v-text="$t('savingsWidget.upload.subtitle')"
      />
    </div>

    <template v-if="conversation.length === 0">
      <savings-upload
        :insurers="insurers"
        :selected-file="selectedFile"
        @drop-file="() => selectedFile = null"
        @open-modal="options => openFormModal(options.mode, options.data)"
        @select-file="file => selectedFile = file"
        @submit="startUpload"
      />
    </template>

    <template v-else>
      <savings-conversation
        :conversation="conversation"
        :file="selectedFile"
        :has-data-button="!!tmpBasket.basketId"
        @open-modal="options => openFormModal(options.mode)"
        @reset="savingsWidgetResetModalRef.open()"
      />
    </template>

    <component
      :is="SavingsFormTemplate"
      ref="savingsWidgetFormModalRef"
      :data="scannedData"
      :insurers="insurers"

      @reset="reset"
      @submit="startConvo"
    />

    <basket-save-modal
      id="save"
      ref="basketSaveModalRef"
      :is-save-without-application-data="false"
      mode="save"
      :source="SOURCE.SAVINGS_WIDGET"
      @saved="basketSaveModalRef.modalRef.close()"
    />

    <basket-save-modal
      id="save"
      ref="offerSendModalRef"
      mode="send"
      :source="SOURCE.SAVINGS_WIDGET"
      @saved="offerSendModalRef.modalRef.close()"
    />

    <modal.container
      id="savingsWidgetResetModal"
      ref="savingsWidgetResetModalRef"
    >
      <modal.step
        id="form"
        footer-alignement="spaced"
        :closeable="false"
        size="midi"
        :step-title="$t('savingsWidget.close.title')"
        type="transactional"

        :primary-label="$t('savingsWidget.close.exit')"
        :secondary-label="$t('form.back')"

        @click-primary="reset"
        @click-secondary="savingsWidgetResetModalRef.modalRef.close()"
      >
        <div
          class="body-2"
          v-html="$t('savingsWidget.close.text')"
        />
      </modal.step>
    </modal.container>
  </div>
</template>

<script setup>
  import { defineAsyncComponent, inject, onMounted, reactive, ref, useTemplateRef } from 'vue'
  import { v4 as uuidv4 } from 'uuid'

  import { events$, loading$ } from '@shared/services'
  import { logInfo } from '@shared/helpers/Logger'

  import basketAPI from '@shared/api/basket'
  import useBasket from '@shared/hooks/useBasket'
  import basketStore from '@shared/store/basket'
  import masterDataAPI from '@shared/api/masterData'
  import useApplication from '@shared/hooks/useApplication'
  import useI18n from '@shared/hooks/useI18n'
  import useSalesAssistant from '@shared/hooks/useSalesAssistant'
  import useStatus from '@shared/hooks/useStatus'

  import { CookieUtil } from '@shared/utils/Cookie'
  import { ReactivityUtil } from '@shared/utils/Reactivity'
  import { addDoctorToBasket } from '@shared/utils/Doctor'

  import BasketSaveModal from '@shared/components/BasketWidget/Modals/BasketSaveModal'
  import SavingsConversation from '@widgets/components/SavingsWidget/SavingsConversation'
  import SavingsUpload from '@widgets/components/SavingsWidget/SavingsUpload'
  const SavingsFormTemplate = defineAsyncComponent(() => import('@widgets/components/SavingsWidget/SavingsForm'))
  import Modal from '@shared/components/Basic/ModalButBetter'

  import { CATEGORY_TYPE, CHANNEL, STORAGE_KEY_SAVINGS } from '@shared/config/constants'
  import { EVENT_BASKET, EVENT_TRACKING, SOURCE } from '@shared/config/events'

  // HOOKS
  const { getDvpUrl } = useApplication()
  const { createDefaultBasket } = useBasket()
  const { locale, selectedLanguage, t } = useI18n()
  const { getInsurers, uploadPolicy } = useSalesAssistant()
  const { basketIsValid } = useStatus()

  // INIT
  const appName$ = inject('appName$')
  const version$ = inject('version$')
  const OUTCOME_ACTIONS = {
    BASKET: {
      label: t('savingsWidget.convo.actions.basket.label'),
      icon: {
        art: 'solid',
        name: 'calculator'
      },
      callback: async () => {
        try {
          loading$.start()

          // @note: if basket is already created (save offer), then only redirect to DVP
          if (!tmpBasket.value.createDate) {
            await basketStore.createOvpBasket(tmpBasket.value)
          }

          events$.emit(EVENT_TRACKING.ENTERED, { source: CHANNEL.SAVINGSWIDGET})

          window.location.href = `${getDvpUrl(selectedLanguage.value)}/basket`

          loading$.end()
        } catch {
          loading$.failed()
        }
      }
    },

    APPOINTMENT: {
      icon: {
        art: 'solid',
        name: 'phone'
      },
      track: {
        as: 'SavingsWidget',
        text: 'Appointment'
      },
      label: t('savingsWidget.convo.actions.appointment.label'),
      callback: () => {
        window.location.href = t('savingsWidget.convo.actions.appointment.link')
      }
    },

    OFFER: {
      label: (mapping) => {
        if (mapping.hasDoctorMissing === true) {
          return t('savingsWidget.convo.actions.offer.labelLight')
        } else {
          return t('savingsWidget.convo.actions.offer.label')
        }
      },
      icon: {
        art: 'solid',
        name: 'envelope'
      },
      callback: async () => {
        try {
          loading$.start()

          if (!tmpBasket.value.createDate) {
            tmpBasket.value = await basketStore.createOvpBasket(tmpBasket.value)
          }

          loading$.end()

          events$.emit(EVENT_BASKET.OPEN_SCREEN_SAVE_MODAL, {
            source: SOURCE.SAVINGS_WIDGET,
          })

          // @note: basket is valid, we can create an offer
          if (basketIsValid(tmpBasket.value)) {
            offerSendModalRef.value.modalRef.open()
          // @note: basket is invalid, e.g. doctor missing, we can only save the basket
          } else {
            basketSaveModalRef.value.modalRef.open()
          }
        } catch {
          loading$.failed()
        }
      }
    }
  }
  const KVG_PRODUCTS = [
    'OTELBProdukt',
    'OTELPProdukt',
    'OMULProdukt',
    'OHAMProdukt',
    'OFRAProdukt',

    'S2CAL1Produkt',
    'ATEL00Produkt',
    'S2MUL1Produkt',
    'A00Produkt',
  ]
  const OUTCOMES = {
    /**
     * all scanned products for all people, are less expensive than the sanitas-comparable product(s) or no sanitas-product is found
     *
     * scannedProducts incl. prices for all added people -> simulate sanitas-comparable product => sanitas is more expensive
     *
     * actions: make appointment
     */
    NO_SAVINGS: {
      id: '4a',
      texts: [0],
      textsAlt: [0],
      actions: [OUTCOME_ACTIONS.APPOINTMENT],
    },

    /**
     * total for scanned is lower than sanitas-total, atleast one person has a cheaper sanitas-comparable or alternative product
     *
     * actions: create basket, send offer (if doctor, otherwise save offer), make appointment
     */
    NEGATIVE_MIXED_POTENTIAL: {
      id: '4b',
      texts: [0],
      textsAlt: [0],
      actions: [OUTCOME_ACTIONS.BASKET, OUTCOME_ACTIONS.OFFER, OUTCOME_ACTIONS.APPOINTMENT],
    },

    /**
     * total for scanned is higher than sanitas-total, but atleast one person (but not everyone) has more cheaper sanitas-comparable product AND atleast one person has an cheaper alternative sanitas-product
     *
     * actions: create baske, send offer (if doctor, otherwise save offer), make appointment
     */
    POSITIVE_MIXED_POTENTIAL: {
      id: '4c',
      texts: [0, 2],
      textsAlt: [1, 2],
      actions: [OUTCOME_ACTIONS.BASKET, OUTCOME_ACTIONS.OFFER, OUTCOME_ACTIONS.APPOINTMENT],
    },

    /**
     * total for scanned is higher than sanitas-total, atleast one (or all) person has savings-potential, but only with an alternative sanitas-kvg-product AND NOT the equivalent-product
     *
     * actions: create basket, send offer (if doctor, otherwise save offer), make appointment
     */
    ALTERNATIVE: {
      id: '4d',
      texts: [0, 2, 3],
      textsAlt: [1, 2, 3],
      actions: [OUTCOME_ACTIONS.BASKET, OUTCOME_ACTIONS.OFFER, OUTCOME_ACTIONS.APPOINTMENT],
    },

    /**
     * total for scanned is higher than sanitas-total, atleast one (or all) person has a cheaper equivalent sanitas-kvg-product
     *
     * actions: create basket, send offer (if doctor, otherwise save offer), make appointment
     */
    EQUIVALENT: {
      id: '4e',
      texts: [0, 1, 3],
      textsAlt: [0, 2, 3],
      actions: [OUTCOME_ACTIONS.BASKET, OUTCOME_ACTIONS.OFFER, OUTCOME_ACTIONS.APPOINTMENT]
    }
  }
  const PRODUCT_MAP_LEGACY = {
    'S2CAL1Produkt': 'OTELBProdukt',
    'ATEL00Produkt': 'OTELPProdukt',
    'S2MUL1Produkt': 'OMULProdukt',
    'A00Produkt': 'OFRAProdukt',
    'OTELBProdukt': 'S2CAL1Produkt',
    'OTELPProdukt': 'ATEL00Produkt',
    'OMULProdukt': 'S2MUL1Produkt',
    'OFRAProdukt': 'A00Produkt',
  }

  // DATA
  const basketSaveModalRef = useTemplateRef('basketSaveModalRef')
  const conversation = reactive([])
  const insurers = ref([])
  const offerSendModalRef = useTemplateRef('offerSendModalRef')
  const savingsWidgetFormModalRef = useTemplateRef('savingsWidgetFormModalRef')
  const savingsWidgetRef = useTemplateRef('savingsWidgetRef')
  const savingsWidgetResetModalRef = useTemplateRef('savingsWidgetResetModalRef')
  const scannedData = { address: {}, conversationId: null, persons: [] }
  const selectedFile = ref(null)
  const tmpBasket = ref({})

  // COMPUTED
  // ...

  // METHODS
  function addLine(data, timeout = 1000) {
    window.setTimeout(() => {
      conversation.push(data)
    }, timeout)
  }

  async function openFormModal(mode, data) {
    if (mode === 'auto') {
      scannedData.conversationId = data.conversationId
      scannedData.persons = []

      // @note: try go get location from zipCode and town/city
      if (data.address) {
        scannedData.address = data.address
      }

      for (const person of data.insuredPersons) {

        const kvgInsurance = person.productRecommendations.find(pr => pr.insuranceType === CATEGORY_TYPE.KVG)

        let docLocation
        if (person.familyDoctor?.postalCode) {
          docLocation = await masterDataAPI.getLocations({
            query: person.familyDoctor?.postalCode
          })
          docLocation = docLocation.find(location => location.town === person.familyDoctor?.city)
        }

        scannedData.persons.push({
          personId: uuidv4(),
          firstName: person.firstName ?? null,
          lastName: person.lastName ?? null,
          dateOfBirth: person.dateOfBirth ?? null,
          gender: person.gender ?? null,

          insurance: [{
            insurer: data?.competitorInsurerName ?? null,
            product: kvgInsurance?.identifiedCompetitorProductName ?? null,
            franchise: kvgInsurance?.deductible ?? null,
            accident: kvgInsurance?.accidentCover ?? null,
            doctor: person?.familyDoctor?.doctor ?? null,
            docName: person?.familyDoctor?.name ?? null,
            docLocation: docLocation ?? null,
            price: kvgInsurance?.competitorPrice ?? null
          }]
        })
      }
    } else if (mode === 'manual') {
      selectedFile.value = null
    }

    savingsWidgetFormModalRef.value.formMode = mode
    savingsWidgetFormModalRef.value.modalRef.open()
  }

  function reset() {
    CookieUtil.delete(STORAGE_KEY_SAVINGS)

    tmpBasket.value = {}
    scannedData.value = { address: {}, conversationId: null, persons: [] }
    selectedFile.value = null
    ReactivityUtil.reAssign(conversation, [])
    savingsWidgetResetModalRef.value.modalRef.close()
  }

  function _addPersonDetails(persons, basket) {
    persons.forEach((person, index) => {
      const _person = {
        contractModified: true,
        order: index + 1,
        personId: person.personId,
        personData: {
          dateOfBirth: person.dateOfBirth,
          firstName: person.firstName,
          lastName: person.lastName,
          gender: person.gender
        },
      }

      if (person.currentRegistration) {
        _person.immigrant = true
        _person.kvgContractStartDate = person.currentRegistration
        _person.preInsurer = {
          currentRegistration: person.currentRegistration,
          preInsurer: false
        }
      }

      basket.persons.push(_person)
    })

    return basket
  }

  function _createSavingsCookie(mapping) {
    const data = {}
    Object.keys(mapping).forEach(personId => {
      data[personId] = {}
      mapping[personId].products.forEach(product => {
        data[personId][product.toSelect.productId] = {
          price: product.thirdParty.price,
          match: !product.altSanitasProduct
        }
      })
    })

    CookieUtil.set({
      key: STORAGE_KEY_SAVINGS,
      value: JSON.stringify(data)
    })
  }

  function _fixProductSelection(mapping, basket) {
    for (const personId of Object.keys(mapping)) {
      const basketPerson = basket.persons.find(p => p.personId === personId)

      KVG_PRODUCTS.forEach(productId => {
        if (basketPerson.products.products[productId]) {
          basketPerson.products.products[productId].selected = false
        }
      })

      mapping[personId].products.forEach(product => {
        const _product = basketPerson.products.products[product.toSelect.productId]
        _product.selected = true

        _product.prices.forEach(p => {
          p.selected = false
        })

        const priceSelection = _product.prices.find(p => p.value === product.toSelect.franchise)

        priceSelection.selected = true
        priceSelection.toggle = product.toSelect.accident
      })
    }

    return basket
  }

  function _getEndMapping(persons, recommendations, basket) {
    let thirdPartyTotal = 0
    let sanitasMatchTotal = 0
    let sanitasAltTotal = 0
    let sanitasTotal = 0

    let savingsMatch = 0
    let altSavingsMatch = 0
    const mappings = {}
    let hasDoctorMissing = false

    for (const person of persons) {
      mappings[person.personId] = {
        totalSanitas: 0,
        totalThirdParty: 0,
        products: []
      }

      for (const product of person.insurance) {
        const basketProducts = basket.persons.find(p => p.personId === person.personId).products.products
        let sanitasProductId = recommendations[person.personId].find(pR => pR.selectedProductName === product.product).sanitasProducts?.[0]

        // @note: done because of https://issue.sanitas.com/browse/FLEX-5335 -> if the stars align, can be removed, when MLE did their work
        if (!basketProducts[sanitasProductId]) {
          sanitasProductId = PRODUCT_MAP_LEGACY[sanitasProductId]
        }
        const simulatedProduct = basketProducts[sanitasProductId].prices.find(p => p.selected)

        // @note: summarize end-total for person
        mappings[person.personId].totalSanitas += parseFloat(simulatedProduct.price)
        mappings[person.personId].totalThirdParty += parseFloat(product.price)

        // @note: if matched product is more expensive than thirdParty product, check, if alternative (other KVG products) is cheaper
        let cheaperAltProductId = null,
            cheaperAltProductPrice = null

        const scannedPrice = parseFloat(product.price)
        if (parseFloat(simulatedProduct.price) > scannedPrice) {
          cheaperAltProductId = KVG_PRODUCTS
            .filter(pId => {
              return pId !== sanitasProductId && basketProducts[pId]
            })
            .sort((a, b) => {
              return parseFloat(basketProducts[a].prices.find(p => p.selected).price) - parseFloat(basketProducts[b].prices.find(p => p.selected).price)
            })
            .find(pId => parseFloat(basketProducts[pId].prices.find(p => p.selected).price) < scannedPrice) ?? null

          if (cheaperAltProductId) {
            cheaperAltProductPrice = parseFloat(basketProducts[cheaperAltProductId]?.prices?.find(p => p.selected)?.price)
          }
        }

        // @note: summarize prices for end-check total
        thirdPartyTotal += scannedPrice
        sanitasMatchTotal += parseFloat(simulatedProduct?.price) ?? 0
        sanitasAltTotal += cheaperAltProductPrice ?? 0
        sanitasTotal += cheaperAltProductPrice > 0 ? cheaperAltProductPrice : parseFloat(simulatedProduct?.price) ?? 0

        let targetProductId = sanitasProductId
        // @note: check, if it's a match or alternatively, if an other kvg-product is cheaper than the thirdParty one
        if (simulatedProduct?.price > 0 && simulatedProduct?.price < product.price) {
          savingsMatch++
        } else if (cheaperAltProductPrice > 0 && cheaperAltProductPrice < product.price) {
          targetProductId = cheaperAltProductId
          altSavingsMatch++
        }

        // @note: hacky way to check, if there's an OHAM-Produkt with missing doctor in the end-result
        if (targetProductId === 'OHAMProdukt' && !product.doctor) {
          hasDoctorMissing = true
        }

        mappings[person.personId].products.push({
          altSanitasProduct: cheaperAltProductId,
          sanitasProduct: sanitasProductId,
          thirdParty: {
            name: product.product,
            price: scannedPrice
          },
          toSelect: {
            productId: targetProductId,
            franchise: product.franchise,
            accident: product.accident ?? false,
            price: parseFloat(cheaperAltProductId ? cheaperAltProductPrice : simulatedProduct.price),
            doctor: product.doctor ?? null,
          }
        })
      }
    }

    logInfo(['thirdPartyTotal', thirdPartyTotal])
    logInfo(['savingsMatch', savingsMatch, sanitasMatchTotal])
    logInfo(['sanitasTotal', sanitasTotal])
    logInfo(['altSavingsMatch', altSavingsMatch, sanitasAltTotal])

    let outcome
    if (savingsMatch === 0 && altSavingsMatch === 0) {
      outcome = OUTCOMES.NO_SAVINGS

    } else if (thirdPartyTotal < sanitasTotal && (savingsMatch > 0 || altSavingsMatch > 0)) {
      outcome = OUTCOMES.NEGATIVE_MIXED_POTENTIAL

    } else if (thirdPartyTotal > sanitasTotal && persons.length > savingsMatch && savingsMatch > 0 && altSavingsMatch > 0) {
      outcome = OUTCOMES.POSITIVE_MIXED_POTENTIAL

    } else if (thirdPartyTotal > sanitasTotal && savingsMatch === 0 && altSavingsMatch > 0) {
      outcome = OUTCOMES.ALTERNATIVE

    } else if (thirdPartyTotal > sanitasTotal && savingsMatch > 0 && altSavingsMatch === 0) {
      outcome = OUTCOMES.EQUIVALENT

    } else {
      throw new Error('OH NOES!')
    }

    return {
      outcome,
      mappings,
      savings: Math.abs((thirdPartyTotal - sanitasTotal) * 12).toFixed(2),
      hasDoctorMissing
    }
  }

  function _selectProductsWithCriteria(persons, basket) {
    for (const person of persons) {
      const basketPerson = basket.persons.find(p => p.personId === person.personId)

      for (const insurance of person.insurance) {
        let productId = insurance.sanitasProduct[0]

        if (!basketPerson.products.products[productId]) {
          productId = PRODUCT_MAP_LEGACY[productId]
        }

        let doctor
        if (insurance.doctor) {
          doctor = {
            address: {
              country: insurance.doctor.country,
              zipCode: insurance.doctor.zipCode,
              town: insurance.doctor.town,
              street: insurance.doctor.street,
              houseNumber: insurance.doctor.houseNumber
            },
            zsrNumber: insurance.doctor.zsrNumber,
            firstName: insurance.doctor.firstName,
            lastName: insurance.doctor.lastName,
            street: insurance.doctor.street,
            houseNumber: insurance.doctor.houseNumber,
            zipCode: insurance.doctor.zipCode,
            town: insurance.doctor.town,
          }
          basket = addDoctorToBasket(basket, doctor)
        }

        const basketProduct = basketPerson.products.products[productId]
        basketProduct.prices.forEach(price => {
          price.selected = false
        })

        if (doctor) {
          basketProduct.doctor = doctor
        }

        const selectedPrice = basketProduct.prices.find(price => price.value === insurance.franchise)
        selectedPrice.selected = true
        selectedPrice.toggle = insurance.accident ?? false

        basketProduct.selected = true
      }
    }

    return basket
  }

  async function startConvo({
    formData = {
      "address": {
        "canton": "ZG",
        "name": "Hünenberg",
        "displayName": "6333 Hünenberg See (Hünenberg)",
        "locationNumber": "1703",
        "town": "Hünenberg See",
        "zipCode": "6333",
        "zipCodeAddon": "00",
        "primeRegion": "ZG_1",
        "latitude": "47.1725464",
        "longitude": "8.449296",
        "country": "CH"
      },
      "conversationId": null,
      "agb": true,
      "persons": [
        {
          "personId": "91ff5891-c2f1-49b0-a4f6-2cab0f593ddd",
          "currentRegistration": null,
          "dateOfBirth": "1994-11-24",
          "firstName": null,
          "gender": "MALE",
          "immigrant": null,
          "lastName": null,
          "insurance": [
            {
              "insurer": "Swica",
              "product": "Standard",
              "franchise": 2500,
              "accident": null,
              "doctor": {
                "boid": "1101538079",
                "zsrNumber": "H103161",
                "lastName": "Walser",
                "firstName": "Thomas",
                "street": "Zypressenstrasse",
                "houseNumber": "50",
                "town": "Zürich",
                "zipCode": "8004",
                "country": "CH",
                "phoneNumber": "+41 44 241 75 55",
                "geo": {
                  "latitude": "47.3773927",
                  "longitude": "8.513108"
                },
                "validFrom": "2024-09-01",
                "validTo": "3000-01-01",
                "medicalOfficeStatus": "ACCEPTS_ALL",
                "medicalOfficeNets": [],
                "careProviderSubtype": {
                  "key": "KomplementaertherapeutInnen",
                  "name": "Complementary medicine therapist"
                }
              },
              "docName": "Thomas Walser",
              "docLocation": {
                "canton": "ZH",
                "name": "Zürich",
                "displayName": "8004 Zürich",
                "locationNumber": "261",
                "town": "Zürich",
                "zipCode": "8004",
                "zipCodeAddon": "00",
                "primeRegion": "ZH_1",
                "latitude": "47.378006",
                "longitude": "8.5233698",
                "country": "CH"
              },
              "price": "250.15",
              "sanitasProduct": [
                "A00Produkt"
              ]
            }
          ]
        }
      ]
    }, recommendations = {
      "91ff5891-c2f1-49b0-a4f6-2cab0f593ddd": [
        {
          "sanitasProducts": [
            "A00Produkt"
          ],
          "insurer": "Swica",
          "selectedProductName": "Standard"
        }
      ]
    }}) {
    savingsWidgetFormModalRef?.value?.modalRef?.modalRef?.close()

    logInfo(['formData', formData])
    logInfo(['recommendations', recommendations])

    ReactivityUtil.reAssign(conversation, [])

    conversation.push({
      title: t('savingsWidget.convo.title'),
      text: t('savingsWidget.convo.busy.0'),
    })

    conversation.push({
      icon: {
        art: 'duo',
        name: 'loader',
      }
    })

    // @note: add scanned product to formData
    for (const personId of Object.keys(recommendations)) {
      const productRecommendations = recommendations[personId]
      const fPerson = formData.persons.find(fP => fP.personId === personId)

      for (const insurance of productRecommendations) {
        const fProduct = fPerson.insurance.find(i => i.product === insurance.selectedProductName)
        fProduct.sanitasProduct = insurance.sanitasProducts
      }
    }

    let simulatedBasket = await createDefaultBasket()
    simulatedBasket.channel = CHANNEL.SAVINGS_WIDGET

    // @note: add address to payload
    simulatedBasket.address = formData.address

    // @note: add persons to payload
    simulatedBasket = _addPersonDetails(formData.persons, simulatedBasket)

    // @note: get initial basket for people
    simulatedBasket = await basketAPI.simulateUpdateBasket({}, simulatedBasket)

    // @note: add selected product with criteria
    simulatedBasket = _selectProductsWithCriteria(formData.persons, simulatedBasket)

    // @note: simulate with new product (and criteria) selection
    simulatedBasket = await basketAPI.simulateUpdateBasket({
      selectSamePriceCombinationForKVG: true
    }, simulatedBasket)

    const _persons = ReactivityUtil.clone(formData.persons)
    const mapping = _getEndMapping(_persons, recommendations, simulatedBasket)
    logInfo(['mapping', mapping])

    // @note: need to build and assign the tmpBasket. first, deselect everything, then select everything from the toSelect-mapping
    tmpBasket.value = _fixProductSelection(mapping.mappings, simulatedBasket)

    // @note: create cookie with mapping for displaying of 'savings' in dvp itself
    _createSavingsCookie(mapping.mappings)

    logInfo(['tmpBasket', tmpBasket.value])

    ReactivityUtil.reAssign(conversation, [])

    const useAltText = mapping.hasDoctorMissing === true
    const messages = mapping.outcome[useAltText ? 'textsAlt' : 'texts']

    // @note: push mapping-results as conversation-lines
    messages.forEach((nr, index) => {
      const line = {
        title: t('savingsWidget.convo.title'),
        text: t(`savingsWidget.convo.${mapping.outcome.id}.${nr}`, {
          savings: mapping.savings
        }),
        actions: []
      }

      if (index + 1 === messages.length) {
        line.actions = mapping.outcome.actions.map(action => {

          // @note: some actions need the end-result to determine the correct label
          if (typeof action.label === 'function') {
            action.label = action.label(mapping)
          }

          return action
        })
      }


      addLine(line, index === 0 ? 0 : 1000 * index)
    })
  }

  async function startUpload() {
    conversation.push({
      title: t('savingsWidget.convo.title'),
      text: t('savingsWidget.convo.busy.0'),
    })

    conversation.push({
      icon: {
        art: 'duo',
        name: 'loader',
      }
    })

    try {
      const data = await uploadPolicy(selectedFile.value)

      // @note: remove loading-message
      conversation.pop()

      // @note: add success-message
      conversation.push({
        title: t('savingsWidget.convo.title'),
        text: t('savingsWidget.convo.uploaded.0'),
        actions: [
          {
            label: t('savingsWidget.convo.actions.form.label'),
            icon: {
              art: 'solid',
              name: 'pencil'
            },
            callback: () => {
              openFormModal('auto', data)
            }
          }
        ]
      })

    } catch {
      // @note: remove loading-message
      conversation.pop()

      // @note: add error-message
      conversation.push({
        title: t('savingsWidget.convo.title'),
        text: t('savingsWidget.convo.error.0'),
      })
    }
  }

  // LIFECYCLE HOOKS
  onMounted(async () => {
    loading$.start()

    /*

    window.setTimeout(() => {
      startConvo({})
    }, 1)

    --------------------------------------------------------------------------

    addLine({ title: 'Spar-Check', text: 'Lorem Ipsum Dolor Sit Amet '}, 0)
    addLine({ title: 'Spar-Check', text: 'Lorem Ipsum Dolor Sit Amet '}, 1000)
    addLine({ title: 'Spar-Check', text: 'Lorem Ipsum Dolor Sit Amet '}, 2000)
    addLine({ title: 'Spar-Check', text: 'Lorem Ipsum Dolor Sit Amet '}, 3000)

    */


    try {
      const data = await getInsurers()

      insurers.value = data.map(insurer => {
        return {
          value: insurer,
          label: insurer
        }
      })

      loading$.end()
    } catch {
      loading$.failed()
    }
  })
</script>

<style scoped>
  #savingsWidget {
    position: relative;
    border-radius: var(--border-radius-res-m);
    background: var(--surface);
    padding: var(--dotcom-responsive-spacing-res-m) var(--dotcom-responsive-gutter-responsive);
    box-shadow: var(--elevation-level-3);
    min-width: 320px;
    margin-left: calc(-1 * var(--dotcom-responsive-offset-responsive));
    margin-right: calc(-1 * var(--dotcom-responsive-offset-responsive));
  }

  .savingsWidget {
    .savingsWidget__header {
      display: flex;
      flex-direction: column;
      gap: var(--dotcom-responsive-spacing-res-xs);
      margin: 0 0 var(--dotcom-responsive-spacing-res-l) 0;

      text-align: center;
    }
  }
</style>

<style scoped>
@media (--v-medium) {
  #savingsWidget {
    margin-left: initial;
    margin-right: initial;
  }
}
</style>
